'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckIcon,
  CircleNotchIcon,
  HashIcon,
  InfoIcon,
  PlusIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import type { TagBatchPreview } from '@/lib/api';
import { splitTagsByScope, tagChipStyle } from '@/lib/transactions/tags';
import { cn, normalizeForSearch } from '@/lib/utils';
import type { TransactionTag } from '@/types';
import { SearchBar } from './SearchBar';

export interface TagAssignSubject {
  id: string;
  /** Display name shown in the header subject line. */
  label: string;
  /** The tag ids the transaction carries now — the pending set's starting point. */
  currentTagIds: readonly string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: TagAssignSubject | null;
  /** The catalog (global + personal), from useTags. */
  tags: readonly TransactionTag[];
  saving: boolean;
  /** Saves the FULL set (replacement — the drawer saves what it shows). */
  onSave: (tagIds: string[], applyBatch: boolean) => Promise<void> | void;
  /** Creates a personal tag from the search text; the drawer pre-selects the result. */
  onCreateTag: (name: string) => Promise<TransactionTag>;
  /** Sizes "Aplicar a lançamentos com a mesma descrição" for the pending set. */
  onPreviewBatch: (subjectId: string, tagIds: string[]) => Promise<TagBatchPreview>;
}

function sameSet(a: ReadonlySet<string>, b: readonly string[]): boolean {
  return a.size === b.length && b.every((id) => a.has(id));
}

/** Pure save-gate (unit-tested like canSaveCategorySelection): an unchanged set never
 *  saves; an armed batch needs a healthy preview with something to update. */
export function canSaveTagSelection(params: {
  hasSubject: boolean;
  changed: boolean;
  saving: boolean;
  applyBatch: boolean;
  preview: TagBatchPreview | null;
  previewLoading: boolean;
}): boolean {
  if (!params.hasSubject || params.saving || !params.changed) return false;
  if (!params.applyBatch) return true;
  if (params.previewLoading || !params.preview) return false;
  return params.preview.updatableCount > 0 && !params.preview.exceedsLimit;
}

/** Pure CTA label (mirrors categorySaveLabel's shape). */
export function tagSaveLabel(params: {
  applyBatch: boolean;
  preview: TagBatchPreview | null;
}): string {
  if (params.applyBatch && params.preview && !params.preview.exceedsLimit && params.preview.updatableCount > 0) {
    return `Atualizar ${params.preview.updatableCount} ${params.preview.updatableCount === 1 ? 'lançamento' : 'lançamentos'}`;
  }
  return 'Salvar tags';
}

/**
 * The tag assignment drawer (VER-378): whole-set editing over the two-scope catalog,
 * with explicit creation (a persistent "Criar nova tag" row plus type-to-create) and
 * the same "mesma descrição" batch contract as the Categorizar drawer.
 */
export function TagAssignDrawer({
  open,
  onOpenChange,
  subject,
  tags,
  saving,
  onSave,
  onCreateTag,
  onPreviewBatch,
}: Props) {
  const [pending, setPending] = useState<Set<string>>(() => new Set(subject?.currentTagIds ?? []));
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [applyBatch, setApplyBatch] = useState(false);
  const [preview, setPreview] = useState<TagBatchPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Scopes the "Criar nova tag" focus jump to THIS drawer's search input — a global
  // selector would find the page's own SearchBar first.
  const searchWrapRef = useRef<HTMLDivElement>(null);

  const subjectId = subject?.id ?? null;
  const currentTagIds = subject?.currentTagIds ?? [];

  // Pending follows the subject's applied set; search/batch state resets on (re)open only.
  useEffect(() => {
    if (open) setPending(new Set(subject?.currentTagIds ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subjectId]);
  useEffect(() => {
    if (open) {
      setQuery('');
      setApplyBatch(false);
    }
  }, [open]);

  const changed = !sameSet(pending, currentTagIds);

  // Batch preview keyed on PRIMITIVES (the sorted pending key), never on wrapper
  // objects — the host recreates callbacks each render, and its loading state
  // re-renders this drawer mid-preview (the CategoryPickerDrawer discipline).
  const pendingKey = useMemo(() => [...pending].sort().join(','), [pending]);
  useEffect(() => {
    if (!open || !applyBatch || !subjectId || !changed) {
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
      return;
    }
    let alive = true;
    setPreviewLoading(true);
    setPreviewError(null);
    onPreviewBatch(subjectId, pendingKey ? pendingKey.split(',') : [])
      .then((p) => {
        if (alive) setPreview(p);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setPreview(null);
        setPreviewError(err instanceof Error ? err.message : 'Não foi possível calcular o lote');
      })
      .finally(() => {
        if (alive) setPreviewLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, applyBatch, subjectId, changed, pendingKey]);

  const filteredTags = useMemo(() => {
    if (query.length === 0) return [...tags];
    const needle = normalizeForSearch(query);
    return tags.filter((t) => normalizeForSearch(t.name).includes(needle));
  }, [tags, query]);

  const sections = useMemo(() => splitTagsByScope(filteredTags), [filteredTags]);

  const normalizedQuery = normalizeForSearch(query.trim());
  const exactMatch = tags.some((t) => normalizeForSearch(t.name) === normalizedQuery);
  const showTypedCreateRow = query.trim().length > 0 && !exactMatch;

  const toggle = (id: string) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await onCreateTag(query.trim());
      setPending((prev) => new Set(prev).add(created.id));
      setQuery('');
    } catch {
      // The host surfaces the failure (toast); the drawer just stays as it was.
    } finally {
      setCreating(false);
    }
  };

  const canSave = canSaveTagSelection({
    hasSubject: subject != null,
    changed,
    saving,
    applyBatch,
    preview,
    previewLoading,
  });

  const renderRow = (tag: TransactionTag) => {
    const style = tagChipStyle(tag);
    const selected = pending.has(tag.id);
    return (
      <li key={tag.id}>
        <button
          type="button"
          onClick={() => toggle(tag.id)}
          aria-pressed={selected}
          aria-label={`${selected ? 'Remover' : 'Selecionar'} #${tag.name}`}
          data-testid={`tag-assign-option-${tag.id}`}
          className={cn(
            'flex w-full min-w-0 items-center gap-3 rounded-xl p-3 text-left transition-colors sm:py-2.5',
            selected ? 'bg-primary/8' : 'hover:bg-surface-sunken',
          )}
        >
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-xl sm:size-9"
            style={{ backgroundColor: style.backgroundColor }}
            aria-hidden
          >
            <HashIcon className="size-4" weight="bold" style={{ color: style.color }} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-base/6 font-semibold text-text-primary sm:text-sm">
                #{tag.name}
              </span>
              {tag.scope === 'global' && (
                <span
                  data-testid="tag-scope-badge"
                  className="shrink-0 rounded-full bg-surface-sunken px-1.5 py-0.5 text-3xs font-medium text-text-tertiary"
                >
                  Vértice
                </span>
              )}
            </span>
          </span>
          <span
            className={cn(
              'flex size-5 shrink-0 items-center justify-center rounded-md border',
              selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong bg-surface',
            )}
            aria-hidden
          >
            {selected && <CheckIcon className="size-3" weight="bold" />}
          </span>
        </button>
      </li>
    );
  };

  const renderSection = (title: string, list: TransactionTag[], testid: string) =>
    list.length > 0 && (
      <>
        <p
          data-testid={testid}
          className="px-3 pt-2 pb-1 text-2xs font-bold tracking-wider text-text-tertiary uppercase"
        >
          {title}
        </p>
        <ul className="space-y-0.5" role="list">
          {list.map(renderRow)}
        </ul>
      </>
    );

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        data-tokens="v2"
        className="flex size-full min-h-0 flex-col overflow-hidden bg-surface px-0 data-[vaul-drawer-direction=bottom]:h-[92dvh] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=right]:size-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="shrink-0 px-5 py-4 text-left! group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left!">
          <DrawerTitle className="text-lg font-semibold text-text-primary">Tags</DrawerTitle>
          <DrawerDescription className="mt-2 flex min-w-0 items-center gap-2 text-sm/5 text-text-secondary sm:text-xs">
            <HashIcon className="size-4 shrink-0" weight="duotone" aria-hidden />
            <span className="truncate">{subject?.label}</span>
          </DrawerDescription>
        </DrawerHeader>

        <div ref={searchWrapRef} className="shrink-0 border-y border-border px-4 py-3 sm:px-5">
          <SearchBar value={query} onChange={setQuery} placeholder="Buscar ou criar tag" ariaLabel="Buscar ou criar tag" />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
          {renderSection('Suas tags', sections.personal, 'tag-section-personal')}
          {renderSection('Tags da Vértice', sections.global, 'tag-section-global')}

          {showTypedCreateRow && (
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating || saving}
              data-testid="tag-assign-create"
              className="mt-0.5 flex w-full items-center gap-3 rounded-xl p-3 text-left text-brand-primary transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-dashed border-border sm:size-9">
                <PlusIcon className="size-4" />
              </span>
              <span className="text-base/6 font-semibold sm:text-sm">
                {creating ? 'Criando…' : `Criar tag “${query.trim()}”`}
              </span>
            </button>
          )}

          {/* Creation is EXPLICIT (VER-378): a persistent affordance, not only a
              type-to-create easter egg. Clicking it sends the user to the search box,
              which is where the name is typed. */}
          {!showTypedCreateRow && query.trim().length === 0 && (
            <button
              type="button"
              onClick={() => searchWrapRef.current?.querySelector('input')?.focus()}
              data-testid="tag-assign-new"
              className="mt-0.5 flex w-full items-center gap-3 rounded-xl p-3 text-left text-brand-primary transition-colors hover:bg-brand-50"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-dashed border-border sm:size-9">
                <PlusIcon className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-base/6 font-semibold sm:text-sm">Criar nova tag</span>
                <span className="mt-0.5 block text-sm/5 text-text-tertiary sm:text-2xs">
                  Digite o nome na busca acima
                </span>
              </span>
            </button>
          )}
        </div>

        {changed && (
          <div className="shrink-0 border-t border-border px-4 py-3 sm:px-5">
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-surface-sunken p-3">
              <span className="group mt-0.5 inline-grid size-5 shrink-0 grid-cols-1 sm:size-4">
                <input
                  type="checkbox"
                  name="tagBatchSameDescription"
                  data-testid="tag-assign-batch"
                  checked={applyBatch}
                  disabled={saving}
                  onChange={(e) => setApplyBatch(e.target.checked)}
                  className="col-start-1 row-start-1 appearance-none rounded-sm border border-border-strong bg-surface checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-primary disabled:border-border disabled:bg-surface-sunken forced-colors:appearance-auto"
                  aria-describedby="tag-batch-same-description-status"
                />
                <svg
                  viewBox="0 0 14 14"
                  fill="none"
                  className="pointer-events-none col-start-1 row-start-1 size-7/8 self-center justify-self-center stroke-white group-not-has-checked:opacity-0"
                  aria-hidden
                >
                  <path d="M3 8L6 11L11 3.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm/5 font-semibold text-text-primary sm:text-xs/5">
                  Aplicar a lançamentos com a mesma descrição
                </span>
                {applyBatch && (
                  <span
                    id="tag-batch-same-description-status"
                    data-testid="tag-batch-note"
                    className={cn(
                      'mt-1 flex items-start gap-1.5 text-sm/5 sm:text-xs/5',
                      previewError || preview?.exceedsLimit ? 'text-warning-text' : 'text-text-secondary',
                    )}
                  >
                    {previewLoading ? (
                      <>
                        <CircleNotchIcon className="mt-0.5 size-4 shrink-0 animate-spin sm:size-3.5" weight="bold" />
                        Calculando lote...
                      </>
                    ) : previewError ? (
                      <>
                        <WarningIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="bold" />
                        {previewError}
                      </>
                    ) : preview?.exceedsLimit ? (
                      <>
                        <WarningIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="bold" />
                        Mais de {preview.limit} lançamentos com essa descrição. Marque uma parte de cada vez.
                      </>
                    ) : preview ? (
                      <>
                        <InfoIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="bold" />
                        {preview.updatableCount > 0
                          ? `${preview.updatableCount} de ${preview.totalMatches} serão atualizados`
                          : 'Nenhum lançamento novo para atualizar'}
                      </>
                    ) : null}
                  </span>
                )}
              </span>
            </label>
          </div>
        )}

        <div className="shrink-0 border-t border-border px-4 pt-3 sm:px-5">
          <button
            type="button"
            data-testid="tag-assign-save"
            onClick={() => void onSave(Array.from(pending), applyBatch)}
            disabled={!canSave}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
          >
            {saving && <CircleNotchIcon className="size-4 animate-spin" weight="bold" />}
            {tagSaveLabel({ applyBatch, preview })}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
